import { CONTACT } from '../constants/brand';
import StaticPage from './StaticPage';

const LAST_UPDATED = 'Última atualização: 28 de maio de 2026';
const PRIVACY_LAST_UPDATED = 'Última atualização: 2 de junho de 2026';

export function SobrePage() {
  return (
    <StaticPage
      eyebrow="Empresa"
      title="Sobre a JurisApp"
      subtitle="Somos uma plataforma focada em produtividade jurídica, com automações para processos, documentos e peticionamento."
      sections={[
        {
          title: 'Nossa missão',
          description: 'Simplificar a rotina do escritório com tecnologia confiável, organizada e orientada a resultados.',
          accent: 'primary',
        },
        {
          title: 'O que entregamos',
          description: 'Centralizamos as rotinas jurídicas para você ter visão, controle e velocidade.',
          items: ['Gestão de processos com visão unificada', 'Automação de petições e anexos', 'Painel de desempenho do escritório'],
        },
        {
          title: 'Segurança e conformidade',
          description: 'Segurança de ponta a ponta, com controle de acesso, trilhas de auditoria e boas práticas de privacidade.',
        },
        {
          title: 'Time próximo do cliente',
          description: 'Atendimento humano e consultivo, com suporte contínuo para suas demandas.',
        },
      ]}
      cta={{ primaryLabel: 'Falar com consultor', primaryTo: '/contato', secondaryLabel: 'Voltar ao início', secondaryTo: '/' }}
    />
  );
}

export function TermsPage() {
  return (
    <StaticPage
      eyebrow="Legal"
      title="Termos de Uso"
      subtitle="Estes Termos regem o acesso e o uso da plataforma JurisApp. Ao criar uma conta ou utilizar os serviços, você declara que leu, entendeu e concorda com as condições abaixo."
      layout="prose"
      lastUpdated={LAST_UPDATED}
      sections={[
        {
          title: '1. Aceitação dos Termos',
          description:
            'Ao acessar ou utilizar o JurisApp, você concorda com estes Termos de Uso e com a nossa Política de Privacidade. Caso não concorde, não utilize a plataforma. O uso é destinado a profissionais e escritórios da advocacia.',
        },
        {
          title: '2. Descrição do serviço',
          description:
            'O JurisApp é uma plataforma de produtividade jurídica que oferece, entre outras funcionalidades:',
          items: [
            'Gestão e consulta de processos, com integração aos sistemas dos tribunais (PDPJ);',
            'Recebimento e organização de comunicações processuais (DJEN);',
            'Armazenamento de documentos e peticionamento eletrônico com assinatura digital;',
            'Gestão de clientes, modelos de documentos e agenda.',
          ],
        },
        {
          title: '3. Cadastro e conta',
          description:
            'Você é responsável pela veracidade das informações fornecidas e pela guarda de suas credenciais de acesso. A autenticação é realizada por provedor de identidade (Auth0). Não compartilhe seu acesso; toda atividade realizada na sua conta é de sua responsabilidade.',
        },
        {
          title: '4. Planos, assinatura e pagamento',
          description:
            'O acesso aos serviços depende de assinatura ativa, conforme o plano contratado. A cobrança é processada por meio de provedor de pagamentos. Períodos de avaliação, valores e condições de renovação ou cancelamento são apresentados no momento da contratação e na área de assinatura.',
        },
        {
          title: '5. Responsabilidades do usuário',
          description: 'Ao utilizar a plataforma, você se compromete a:',
          items: [
            'Utilizar os serviços de acordo com a legislação aplicável e a ética profissional;',
            'Não inserir conteúdo ilícito, nem violar direitos de terceiros;',
            'Manter atualizados certificados digitais e dados necessários ao peticionamento;',
            'Conferir, no portal oficial do tribunal, os atos cujo resultado dependa de protocolo.',
          ],
        },
        {
          title: '6. Limitação de responsabilidade',
          description:
            'O JurisApp atua como ferramenta de apoio e não substitui a análise profissional do advogado, tampouco os sistemas oficiais dos tribunais. Não nos responsabilizamos por indisponibilidades de serviços de terceiros (tribunais, provedores de pagamento ou de identidade) nem por decisões tomadas com base nas informações exibidas. O usuário permanece responsável por prazos e atos processuais.',
        },
        {
          title: '7. Propriedade intelectual',
          description:
            'A marca, o software, o design e os demais elementos da plataforma pertencem ao JurisApp e são protegidos por lei. O conteúdo que você insere (documentos, dados de clientes e processos) permanece de sua titularidade; concedemos a você uma licença de uso da plataforma, e você nos autoriza a processar esse conteúdo para a prestação do serviço.',
        },
        {
          title: '8. Suspensão e encerramento',
          description:
            'Podemos suspender ou encerrar o acesso em caso de violação destes Termos ou de uso indevido. Você pode encerrar sua conta a qualquer momento. Após o encerramento, os dados serão tratados conforme a Política de Privacidade e a legislação aplicável.',
        },
        {
          title: '9. Alterações dos Termos',
          description:
            'Estes Termos podem ser atualizados periodicamente. Alterações relevantes serão comunicadas pelos canais da plataforma. O uso continuado após a vigência das alterações implica concordância.',
        },
        {
          title: '10. Foro e contato',
          description: `Estes Termos são regidos pela legislação brasileira. Dúvidas podem ser enviadas para ${CONTACT.email}.`,
        },
      ]}
      cta={{ primaryLabel: 'Falar com a equipe', primaryTo: '/contato', secondaryLabel: 'Voltar ao início', secondaryTo: '/' }}
    />
  );
}

export function PrivacyPage() {
  return (
    <StaticPage
      eyebrow="Legal"
      title="Política de Privacidade"
      subtitle="Esta Política descreve como o JurisApp coleta, utiliza, armazena e protege dados pessoais, em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018 — LGPD)."
      layout="prose"
      lastUpdated={PRIVACY_LAST_UPDATED}
      sections={[
        {
          title: '1. Quem é o controlador',
          description: `O JurisApp é o controlador dos dados pessoais tratados na plataforma. Para questões de privacidade, fale com nosso Encarregado (DPO) pelo e-mail ${CONTACT.email}.`,
        },
        {
          title: '2. Dados que coletamos',
          description: 'Tratamos as seguintes categorias de dados:',
          items: [
            'Dados cadastrais do usuário (nome, e-mail, OAB e dados de contato);',
            'Dados de uso e acesso (logs, endereço IP, dispositivo e registros de auditoria);',
            'Conteúdo inserido na plataforma (processos, documentos, dados de clientes e petições);',
            'Dados de assinatura e pagamento, processados por provedor especializado.',
          ],
        },
        {
          title: '3. Para que utilizamos',
          description: 'Os dados são utilizados para:',
          items: [
            'Prestar e operar os serviços contratados;',
            'Autenticar usuários e garantir a segurança da conta;',
            'Integrar com os sistemas dos tribunais e processar peticionamento;',
            'Enviar comunicações operacionais e de suporte;',
            'Cumprir obrigações legais e regulatórias.',
          ],
        },
        {
          title: '4. Bases legais',
          description:
            'O tratamento se fundamenta, conforme o caso, na execução de contrato, no cumprimento de obrigação legal, no legítimo interesse e, quando aplicável, no consentimento do titular.',
        },
        {
          title: '5. Compartilhamento',
          description:
            'Não vendemos dados pessoais. Compartilhamos dados apenas com operadores e parceiros necessários à prestação do serviço (por exemplo, provedor de identidade, provedor de pagamento, infraestrutura em nuvem e integrações com tribunais), sempre limitados à finalidade e sob obrigações de confidencialidade e segurança.',
        },
        {
          title: '6. Integração com o Google Calendar',
          description:
            'A sincronização com o Google Calendar é opcional e só ocorre se você autorizar expressamente o acesso à sua conta Google. Ao conectar, solicitamos permissão para gerenciar os eventos da sua agenda (escopo do Google Calendar), nas seguintes condições:',
          items: [
            'Dados acessados: eventos e calendários da sua conta Google, utilizados exclusivamente para sincronizar a sua agenda no JurisApp com o Google Calendar (ler e criar, editar ou excluir os eventos correspondentes);',
            'Armazenamento: guardamos apenas os tokens de acesso necessários à sincronização, sempre criptografados, e não retemos o conteúdo do seu calendário além do estritamente necessário para a sincronização;',
            'Compartilhamento: não vendemos nem compartilhamos com terceiros os dados obtidos do Google, e não os utilizamos para publicidade nem para treinar modelos de inteligência artificial;',
            'Uso limitado: o uso das informações recebidas das APIs do Google adere à Política de Dados de Usuário dos Serviços de API do Google (Google API Services User Data Policy), inclusive aos requisitos de Uso Limitado (Limited Use);',
            'Revogação: você pode desconectar a integração a qualquer momento no próprio JurisApp ou em myaccount.google.com/permissions, o que encerra o acesso e remove os tokens armazenados.',
          ],
        },
        {
          title: '7. Armazenamento e segurança',
          description:
            'Adotamos medidas técnicas e organizacionais para proteger os dados, incluindo criptografia, controle de acesso por perfil e trilhas de auditoria. Documentos e certificados sensíveis são protegidos por criptografia. Os dados são mantidos pelo tempo necessário às finalidades e às obrigações legais.',
        },
        {
          title: '8. Direitos do titular',
          description:
            'Você pode solicitar, a qualquer momento, confirmação de tratamento, acesso, correção, anonimização, portabilidade, eliminação e informações sobre compartilhamento, nos termos da LGPD. As solicitações são atendidas pelos nossos canais de contato.',
        },
        {
          title: '9. Transferência internacional',
          description:
            'Caso haja transferência de dados para fora do Brasil (por exemplo, em razão de provedores de nuvem), garantimos que o destino ofereça grau de proteção adequado, conforme a LGPD.',
        },
        {
          title: '10. Alterações e contato',
          description: `Esta Política pode ser atualizada periodicamente. Em caso de dúvidas sobre privacidade e proteção de dados, fale com o Encarregado pelo e-mail ${CONTACT.email}.`,
        },
      ]}
      cta={{ primaryLabel: 'Falar com o Encarregado (DPO)', primaryTo: '/contato', secondaryLabel: 'Voltar ao início', secondaryTo: '/' }}
    />
  );
}

export function LgpdPage() {
  return (
    <StaticPage
      eyebrow="Legal"
      title="LGPD"
      subtitle="O JurisApp trata dados pessoais em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018). Esta página resume nosso programa de governança; o detalhamento do tratamento está na Política de Privacidade."
      layout="prose"
      lastUpdated={LAST_UPDATED}
      sections={[
        {
          title: 'Princípios',
          description:
            'Pautamos o tratamento de dados pela finalidade, adequação, necessidade, transparência, segurança e prestação de contas, tratando apenas os dados necessários às finalidades informadas.',
        },
        {
          title: 'Encarregado (DPO)',
          description: `Mantemos um Encarregado pelo tratamento de dados pessoais, responsável por receber solicitações de titulares e comunicações da autoridade. Contato: ${CONTACT.email}.`,
        },
        {
          title: 'Direitos dos titulares',
          description: 'Asseguramos o exercício dos direitos previstos no art. 18 da LGPD, incluindo:',
          items: [
            'Confirmação da existência de tratamento e acesso aos dados;',
            'Correção de dados incompletos, inexatos ou desatualizados;',
            'Anonimização, bloqueio ou eliminação de dados desnecessários;',
            'Portabilidade e informação sobre compartilhamento;',
            'Revogação do consentimento, quando essa for a base legal.',
          ],
        },
        {
          title: 'Segurança e incidentes',
          description:
            'Adotamos medidas técnicas e organizacionais para proteger os dados e mantemos processos para resposta a incidentes. Eventuais incidentes de segurança relevantes são tratados conforme a LGPD e comunicados quando exigido.',
        },
        {
          title: 'Como exercer seus direitos',
          description: `Para exercer seus direitos ou tirar dúvidas sobre conformidade, entre em contato pelo e-mail ${CONTACT.email} ou pela página de contato.`,
        },
      ]}
      cta={{ primaryLabel: 'Falar com o Encarregado (DPO)', primaryTo: '/contato', secondaryLabel: 'Ver Política de Privacidade', secondaryTo: '/privacidade' }}
    />
  );
}

export function CookiesPage() {
  return (
    <StaticPage
      eyebrow="Legal"
      title="Política de Cookies"
      subtitle="Esta página explica como o JurisApp utiliza cookies e tecnologias semelhantes para operar a plataforma e melhorar sua experiência."
      layout="prose"
      lastUpdated={LAST_UPDATED}
      sections={[
        {
          title: 'O que são cookies',
          description:
            'Cookies são pequenos arquivos armazenados no seu navegador. Também utilizamos tecnologias semelhantes, como armazenamento local (localStorage/IndexedDB), necessárias ao funcionamento offline da plataforma.',
        },
        {
          title: 'Cookies essenciais',
          description:
            'Garantem o funcionamento básico e a segurança — autenticação, manutenção da sessão e proteção contra fraudes. Sem eles, a plataforma não funciona corretamente, por isso não podem ser desativados.',
        },
        {
          title: 'Funcionais e de preferência',
          description:
            'Armazenam preferências como tema (claro/escuro) e dados de uso offline (cache de páginas e documentos), melhorando a experiência e o desempenho.',
        },
        {
          title: 'Analíticos',
          description:
            'Quando utilizados, ajudam a entender o uso da plataforma de forma agregada, para aprimorar funcionalidades e estabilidade. Não utilizamos cookies para publicidade.',
        },
        {
          title: 'Como gerenciar',
          description:
            'Você pode bloquear ou apagar cookies nas configurações do seu navegador. A desativação de cookies essenciais pode comprometer o acesso e o uso da plataforma.',
        },
        {
          title: 'Dúvidas',
          description: `Para mais informações sobre o uso de cookies e dados pessoais, consulte a Política de Privacidade ou escreva para ${CONTACT.email}.`,
        },
      ]}
      cta={{ primaryLabel: 'Ver Política de Privacidade', primaryTo: '/privacidade', secondaryLabel: 'Voltar ao início', secondaryTo: '/' }}
    />
  );
}
